import React from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQAccordion from '../../components/accordion';
import AccordionItem from '../../components/accordion/accordion-item';
import UQCard from '../../components/card';
import UQCode from '../../components/code';
import UQSpacer from '../../components/spacer';
import { BREADCRUMB_ROOT } from '../../common/config';
import UQRow from '../../components/row';
import UQCol from '../../components/col';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';

const AccordionPage = () => {
  const groupedAccordionCode = `
import React from 'react';
import { UQAccordion, AccordionItem } from '@uniqconsulting/ui';

<UQAccordion singleExpand>
  <AccordionItem title="Section 1">
    <p>This is the content of Section 1.</p>
  </AccordionItem>
  <AccordionItem title="Section 2">
    <p>This is the content of Section 2.</p>
  </AccordionItem>
  <AccordionItem title="Section 3">
    <p>This is the content of Section 3.</p>
  </AccordionItem>
</UQAccordion>
`;

  const separateAccordionCode = `
import React from 'react';
import { UQAccordion, AccordionItem } from '@uniqconsulting/ui';

<UQAccordion singleExpand={false} separatePanels>
  <AccordionItem title="Section 1">
    <p>This is the content of Section 1.</p>
  </AccordionItem>
  <AccordionItem title="Section 2">
    <p>This is the content of Section 2.</p>
  </AccordionItem>
  <AccordionItem title="Section 3">
    <p>This is the content of Section 3.</p>
  </AccordionItem>
</UQAccordion>
`;

  const attributes = [
    {
      attribute: 'singleExpand',
      type: 'boolean',
      default: 'false',
      description: 'Allows only one panel to be expanded at a time.',
    },
    {
      attribute: 'separatePanels',
      type: 'boolean',
      default: 'false',
      description: 'Adds spacing between accordion panels.',
    },
    {
      attribute: 'children',
      type: 'React.ReactNode',
      default: 'undefined',
      description: 'The content of the accordion, typically `AccordionItem` components.',
    },
  ];

  const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
    {
      columnId: 'attribute',
      renderHeaderCell: () => 'Attribute',
      renderCell: (item) => item.attribute,
    },
    {
      columnId: 'type',
      renderHeaderCell: () => 'Type',
      renderCell: (item) => <code>{item.type}</code>,
    },
    {
      columnId: 'default',
      renderHeaderCell: () => 'Default',
      renderCell: (item) => <code>{item.default}</code>,
    },
    {
      columnId: 'description',
      renderHeaderCell: () => 'Description',
      renderCell: (item) => item.description,
    },
  ];

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Accordion']} />
      <h1>UQAccordion</h1>
      <UQSpacer size="s" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQAccordion</code> component is a collapsible panel system that allows you to group content into expandable sections. It supports single or multiple expanded panels and can be customized with spacing between panels.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
      />
      <UQSpacer size="m" />

      {/* Grouped Panels Example */}
      <h2>Grouped Panels</h2>
      <UQSpacer size="s" />
      <UQRow>
        <UQCol span={12}>
          <UQAccordion singleExpand={true}>
            <AccordionItem title="Section 1">
              <p>This is the content of Section 1.</p>
            </AccordionItem>
            <AccordionItem title="Section 2">
              <p>This is the content of Section 2.</p>
            </AccordionItem>
            <AccordionItem title="Section 3">
              <p>This is the content of Section 3.</p>
            </AccordionItem>
          </UQAccordion>
          <UQSpacer size="s" />
          <UQCard>
            <h2>Code Example</h2>
            <UQSpacer size="m" />
            <UQCode code={groupedAccordionCode} enableCopy />
          </UQCard>
        </UQCol>
      </UQRow>
      <UQSpacer />

      {/* Separate Panels Example */}
      <h2>Separate Panels</h2>
      <UQSpacer size="s" />
      <UQRow>
        <UQCol span={12}>
          <UQAccordion singleExpand={false} separatePanels={true}>
            <AccordionItem title="Section 1">
              <p>This is the content of Section 1.</p>
            </AccordionItem>
            <AccordionItem title="Section 2">
              <p>This is the content of Section 2.</p>
            </AccordionItem>
            <AccordionItem title="Section 3">
              <p>This is the content of Section 3.</p>
            </AccordionItem>
          </UQAccordion>
          <UQSpacer size="s" />
          <UQCard>
            <h2>Code Example</h2>
            <UQSpacer size="m" />
            <UQCode code={separateAccordionCode} enableCopy />
          </UQCard>
        </UQCol>
      </UQRow>
    </>
  );
};

export default AccordionPage;
